import React, { useState, useEffect, useRef } from 'react';
import './product_audit.css';
import Trial from '../Trial';
import { Link } from 'react-router-dom';

/* ── animated counter hook ── */
function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const stats = [
  { value: 5000, suffix: '+', label: 'Businesses Trusted' },
  { value: 99, suffix: '%', label: 'GST Accuracy Rate' },
  { value: 7, suffix: ' Days', label: 'Free Trial' },
  { value: 24, suffix: '/7', label: 'Support Available' },
];

const overviewData = [
  {
    icon: '📊',
    gradient: 'linear-gradient(135deg,#667eea,#764ba2)',
    imgSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
    title: 'Accounting, Inventory & e-Invoicing – Simplified',
    description:
      'Our all-in-one billing platform integrates Accounting, Inventory, and e-Invoicing to streamline operations. Automate invoices, track payments, manage stock, and boost profitability—effortlessly.',
    tags: ['Accounting', 'e-Invoice', 'Inventory'],
  },
  {
    icon: '🧾',
    gradient: 'linear-gradient(135deg,#f093fb,#f5576c)',
    imgSrc: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80',
    title: 'Integrated GST Reporting System',
    description:
      'File accurate GST returns with ease using our intelligent reporting suite. Access GSTR-1, GSTR-3B, and more—validated in real time. Reduce errors, save time, and stay fully compliant.',
    tags: ['GSTR-1', 'GSTR-3B', 'Real-time'],
  },
  {
    icon: '🔄',
    gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)',
    imgSrc: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80',
    title: 'Import & Export Data',
    description:
      'Seamlessly transfer information to and from various sources. Whether migrating data from existing systems, collaborating with partners, or maintaining backups—our import/export engine handles it all.',
    tags: ['Excel', 'CSV', 'Migration'],
  },
  {
    icon: '📱',
    gradient: 'linear-gradient(135deg,#43e97b,#38f9d7)',
    imgSrc: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80',
    title: 'Manage Your Business Anywhere',
    description:
      'Stay connected, monitor performance, and make smart decisions on the go—whether at the office, home, or traveling. Real-time access to data and seamless communication, anywhere.',
    tags: ['Mobile', 'Cloud', 'Remote'],
  },
];

const faqData = [
  { question: 'What is Auditusierp?', answer: 'Auditusierp is an all-in-one ERP platform for accounting, inventory, GST, and business reporting.' },
  { question: 'Can I access Auditusierp remotely?', answer: 'Yes, it is cloud-based and accessible from any device with an internet connection.' },
  { question: 'Is Auditusierp GST compliant?', answer: 'Absolutely. It supports real-time GST filing, including GSTR-1, GSTR-3B, and more.' },
  { question: 'How does Auditusierp handle data import/export?', answer: 'It allows seamless data migration and supports bulk import/export to Excel and other formats.' },
  { question: 'Can I manage multiple companies in Auditusierp?', answer: 'Yes, it supports multi-company and multi-user environments with role-based permissions.' },
  { question: 'Does Auditusierp offer inventory management?', answer: 'Yes, it provides live inventory tracking, stock alerts, and barcode scanning support.' },
  { question: 'Is there a mobile app for Auditusierp?', answer: 'Yes, mobile apps are available for both Android and iOS, offering real-time access on the go.' },
  { question: 'How customizable is Auditusierp?', answer: 'It includes customizable invoice formats, report templates, and user-defined settings.' },
  { question: 'What kind of support is available?', answer: 'You get email, chat, and phone support, along with training and onboarding help.' },
  { question: 'Is my data safe in Auditusierp?', answer: 'Yes, your data is encrypted, regularly backed up, and hosted on secure servers.' },
];

export default function Productoverview() {
  const [openIndex, setOpenIndex] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <div className="po-root">
      {/* ── HERO ── */}
      <section className="po-hero">
        <div className="po-hero-orb po-hero-orb-1" />
        <div className="po-hero-orb po-hero-orb-2" />
        <div className="po-hero-orb po-hero-orb-3" />
        <div className="po-hero-inner">
          <div className="po-hero-badge">
            <span className="po-badge-dot" />
            All-in-One ERP Platform
          </div>
          <h1 className="po-hero-title">
            Meet <span className="po-hero-gradient">Auditusierp</span>
            <br />Your Complete Business OS
          </h1>
          <p className="po-hero-sub">
            From managing books to filing GST returns, tracking inventory to generating
            insightful reports — Auditusierp is your all-in-one growth partner built for
            modern Indian businesses.
          </p>
          <div className="po-hero-actions">
            <Link to="/Demo" className="po-btn-primary">
              <span>Start Free Trial</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <Link to="/Demo" className="po-btn-ghost">Watch Demo</Link>
          </div>
          <div className="po-hero-image-wrap">
            <div className="po-hero-glow" />
            <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80" alt="Auditusierp dashboard overview" className="po-hero-img" />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="po-stats" ref={statsRef}>
        {stats.map((s, i) => (
          <StatCard key={i} {...s} start={statsVisible} />
        ))}
      </section>

      {/* ── BANNER STRIP ── */}
      <section className="po-banner-strip">
        <img src="https://images.unsplash.com/photo-1556155092-490a1ba16284?w=800&q=80" className="po-banner-img" alt="ERP Analytics Dashboard" />
        <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80" className="po-banner-img" alt="Inventory Management System" />
      </section>

      {/* ── FEATURE CARDS ── */}
      <section className="po-features">
        <div className="po-section-header">
          <span className="po-section-tag">Core Modules</span>
          <h2 className="po-section-title">Everything You Need to Run Your Business</h2>
          <p className="po-section-sub">Four pillars powering thousands of businesses across India</p>
        </div>
        <div className="po-cards-grid">
          {overviewData.map((item, index) => (
            <OverviewCard key={index} item={item} index={index} />
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="po-faq">
        <div className="po-faq-inner">
          <div className="po-section-header">
            <span className="po-section-tag">FAQ</span>
            <h2 className="po-section-title">Frequently Asked Questions</h2>
            <p className="po-section-sub">Everything you need to know about Auditusierp</p>
          </div>
          <div className="po-faq-list">
            {faqData.map((item, index) => (
              <div
                key={index}
                className={`po-faq-item ${openIndex === index ? 'open' : ''}`}
              >
                <button className="po-faq-q" onClick={() => toggleFAQ(index)}>
                  <span>{item.question}</span>
                  <span className="po-faq-icon">{openIndex === index ? '−' : '+'}</span>
                </button>
                <div className="po-faq-a">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Trial />
    </div>
  );
}

function StatCard({ value, suffix, label, start }) {
  const count = useCountUp(value, 1800, start);
  return (
    <div className="po-stat-card">
      <span className="po-stat-num">{count}{suffix}</span>
      <span className="po-stat-label">{label}</span>
    </div>
  );
}

function OverviewCard({ item, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`po-card ${visible ? 'po-card--visible' : ''}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="po-card-accent" style={{ background: item.gradient }} />
      <div className="po-card-icon-wrap" style={{ background: item.gradient }}>
        <span className="po-card-icon">{item.icon}</span>
      </div>
      <div className="po-card-img-wrap">
        <img src={item.imgSrc} alt={item.title} className="po-card-img" />
      </div>
      <div className="po-card-body">
        <h3 className="po-card-title">{item.title}</h3>
        <p className="po-card-desc">{item.description}</p>
        <div className="po-card-tags">
          {item.tags.map((t, i) => (
            <span key={i} className="po-card-tag">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
